import React, {useEffect, useState} from "react";
import MiniTable from "./MiniTable";
import styles from "./MiniTableContainer.module.css"
import {PageResponse} from "../../../types/response";

interface Column<T> {
    key: string;
    header: string;
    width?: string;
    render?: (value: T) => React.ReactNode;
}

interface TableTab<T = any> {
    id: string;
    label: string;
    fetchData: (page: number) => Promise<PageResponse<T[]>>;
    columns: Column<T>[];
}

interface MiniTableContainerProps {
    tabs: TableTab[];
    size?: 'small' | 'medium' | 'large';
    itemsPerPage?: number;
    onRowClick?: (row: any) => void;
    className?: string;
}

// 미니 테이블 컨테이너 컴포넌트
const MiniTableContainer: React.FC<MiniTableContainerProps> = ({
                                                                   tabs,
                                                                   size = 'medium',
                                                                   itemsPerPage = 3,
                                                                   onRowClick,
                                                                   className= ''
                                                               }) => {
    const [activeTabId, setActiveTabId] = useState<string>(tabs[0].id);
    const [data, setData] = useState<any[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [currentPage, setCurrentPage] = useState<number>(1);
    const [totalPages, setTotalPages] = useState<number>(1);

    const activeTab = tabs.find(tab => tab.id === activeTabId)!;

    //테이블 데이터 설정
    const fetchTableData = async (page: number) => {
        try {
            setLoading(true);
            setError(null);
            const response = await activeTab.fetchData(page);
            setData(response.content);
            setTotalPages(Math.ceil(response.total / itemsPerPage));
        } catch (err) {
            setError(err instanceof Error ? err.message : '데이터 로딩 실패');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchTableData(currentPage);
    }, [activeTabId, currentPage]);

    // 테이블 탭 변경
    const handleTabChange = (tabId: string) => {
        setActiveTabId(tabId);
        setCurrentPage(1);
    };

    // 테이블 페이지 변경
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };

    // 탭 버튼
    const headerTabs = tabs.map(tab => ({
        label: tab.label,
        active: tab.id === activeTabId,
        onClick: () => handleTabChange(tab.id)
    }));

    if (error) {
        return <div className={styles.error}>{error}</div>;
    }

    return (
        <div className={styles.container}>
            {loading ? (
                <div className={styles.loading}>로딩 중...</div>
            ) : (
                // 미니 테이블 컴포넌트
                <MiniTable
                    columns={activeTab.columns}
                    data={data}
                    size={size}
                    showPagination={true}
                    page={currentPage}
                    totalPages={totalPages}
                    onPageChange={handlePageChange}
                    headerTabs={headerTabs}
                    onRowClick={onRowClick}
                    className={className}
                />
            )}
        </div>
    );
};

export default MiniTableContainer;